import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * 換頁進度條
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼骨架屏不夠 ────────────────────────────────────────
 *
 * App.tsx 的 Suspense 包住的是**整個** Routes，連 Shell（頁首與
 * 頁尾）都在裡面。所以換頁的時候骨架屏會把整片版面換掉，包含
 * 使用者剛剛按下的那個導覽列 —— 他失去了「我按到哪裡」的參照，
 * 而畫面重排的幅度讓一次換頁看起來像一次重新載入。
 *
 * 這條進度條解決的是另一半：它固定在最上緣，不隨路由卸載，
 * 所以從按下到新頁畫出來之間，畫面上一直有一個連續的東西在動。
 *
 * ── 為什麼進度是假的，而且刻意不到 100% ────────────────────
 *
 * 我們並不知道還要多久：chunk 多大、網路多慢、資料要抓幾次，
 * 一個都不知道。所以它先快後慢地爬向 90% 然後停住 —— 停住是
 * 誠實的，它表示「還在等」。真正到 100% 的唯一時機是新頁面
 * 真的畫出來了（見 useRouteReady）。
 *
 * 一條會自己跑到 100% 然後停在那裡的進度條是在說謊，而使用者
 * 只要被騙過一次，之後就再也不看它了。
 *
 * ── 為什麼延遲 120 毫秒才出現 ──────────────────────────────
 *
 * 已經下載過的 chunk 換頁只要幾十毫秒。在那種情況下顯示進度條，
 * 使用者看到的是一道閃光 —— 而閃光讀起來像「出事了」，
 * 不像「載入中」。快到看不見的事情就不該被通知。
 */
export default function NavigationProgress() {
  const location = useLocation();
  const [progress, setProgress] = useState<number | null>(null);
  const timers = useRef<number[]>([]);

  const clear = () => {
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
  };

  useEffect(() => {
    clear();
    setProgress(null);

    /* 120ms 之後才開始 —— 快取命中的換頁不該閃一下 */
    timers.current.push(
      window.setTimeout(() => setProgress(8), 120),
      window.setTimeout(() => setProgress(38), 220),
      window.setTimeout(() => setProgress(64), 480),
      window.setTimeout(() => setProgress(82), 900),
      window.setTimeout(() => setProgress(90), 1600),
    );

    /*
      清理函式在**下一次**location 變更前跑，而不是在頁面畫好時 ——
      收尾交給 useRouteReady 送出的事件（見下面）。
    */
    return clear;
  }, [location.key]);

  useEffect(() => {
    function finish() {
      clear();
      setProgress(100);
      /* 讓 100% 的那一格看得見再收掉，否則等於沒有終點 */
      timers.current.push(window.setTimeout(() => setProgress(null), 260));
    }
    window.addEventListener(ROUTE_READY, finish);
    return () => window.removeEventListener(ROUTE_READY, finish);
  }, []);

  if (progress === null) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]"
      role="progressbar"
      aria-hidden
    >
      <div
        className="h-full rounded-r-pill bg-gradient-to-r from-brand-400 to-brand-600"
        style={{
          width: `${progress}%`,
          /*
            width 用 transition，不是 animation —— 每一段的目標值
            由上面的計時器給，中間的補間交給瀏覽器的合成執行緒。
            那條執行緒不會被 chunk 的解析卡住，而換頁時主執行緒
            正好在忙那件事。
          */
          transition: progress === 100 ? "width 180ms ease-out" : "width 400ms ease-out",
          boxShadow: "0 0 8px var(--color-brand-500)",
        }}
      />
    </div>
  );
}

/** 新頁面畫好了 */
const ROUTE_READY = "colouredev:route-ready";

/**
 * 掛在 Suspense **裡面**，用來宣告「這一頁真的畫出來了」。
 *
 * ── 為什麼一定要在 Suspense 裡面 ────────────────────────────
 *
 * 邊界被暫停的時候，裡面的東西一個都不會 commit ——
 * 所以放在裡面的 effect 不會跑。等到 chunk 到了、邊界解除，
 * 它才跟著新頁面一起 commit，那一刻正是「畫出來了」。
 *
 * 放在外面的話，effect 會在 chunk 還在下載時就跑掉，
 * 進度條會在頁面出現之前就先跑完 —— 那比沒有進度條更糟，
 * 因為它先說完成了，然後畫面才開始等。
 */
export function useRouteReady(key: string) {
  useEffect(() => {
    window.dispatchEvent(new Event(ROUTE_READY));
  }, [key]);
}

/** useRouteReady 的元件版，方便直接放進 JSX */
export function RouteReady() {
  const location = useLocation();
  useRouteReady(location.key);
  return null;
}
